import { FC } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import {
  BetSystemSettingsPage,
  DailyBonusesPage,
  ReferralRewardSettingsPage,
  TicketSettingsPage,
  VipSettingsPage,
} from 'main';
import { RoleGuard } from 'v2/components/roleGuard/RoleGuard';
import {
  ALL_ADMIN_GROUP,
  MANAGEMENT_GROUP,
  SELLS_GROUP,
  HEAD_SUPPORT_GROUP,
  SERVICE_GROUP,
  PRIZE_SERVICE_GROUP,
  WRITE_GROUP,
  SUPPORT_GROUP,
  PRIZE_HEAD_SUPPORT_GROUP,
} from 'v2/constants';
import { AppLayout } from 'v2/layout/appLayout/AppLayout';
import { ErrorLayout } from 'v2/layout/errorLayout/ErrorLayout';
import { AdsSettingsPage } from 'v2/pages/ads/AdsSettingsPage';
import { BroadcastNotificationsPage } from 'v2/pages/broadcastNotifications/BroadcastNotificationsPage';
import { CasinoAdsPage } from 'v2/pages/casinoAds/CasinoAdsPage';
import { CasinoSettingsPage } from 'v2/pages/casinoSettings/CasinoSettingsPage';
import { CheatersPage } from 'v2/pages/cheaters/CheatersPage';
import { DistributionLinkPage } from 'v2/pages/distributionLink/DistributionLinkPage';
import { DomainPage } from 'v2/pages/domain/DomainPage';
import { EntryRewardPage } from 'v2/pages/entryReward/EntryRewardPage';
import { FileSystemPage } from 'v2/pages/fileSystem/FileSystemPage';
import { FreeSpinsPage } from 'v2/pages/freeSpins/FreeSpinsPage';
import { LevelsPage } from 'v2/pages/levels/LevelsPage';
import { LoginPage } from 'v2/pages/login/LoginPage';
import { LotteriesPage } from 'v2/pages/lotteries/LotteriesPage';
import { MailingPage } from 'v2/pages/mailing/MailingPage';
import { MainScreenSettingsPage } from 'v2/pages/mainScreenSettings/MainScreenSettingsPage';
import { MasterKeyPage } from 'v2/pages/masterKey/MasterKeyPage';
import { ModalSettingsPage } from 'v2/pages/modalSettings/ModalSettingsPage';
import { PartnersPage } from 'v2/pages/partners/PartnersPage';
import { PromoCodePage } from 'v2/pages/promoCode/PromoCodePage';
import { ProviderTransactionsPage } from 'v2/pages/providerTransactions/ProviderTransactionsPage';
import { ReferralSystemPage } from 'v2/pages/referralSystem/ReferralSystemPage';
import { RewardsHistoryPage } from 'v2/pages/rewardsHistory/RewardsHistoryPage';
import { RootPage } from 'v2/pages/root/RootPage';
import { SlotsSettingsPage } from 'v2/pages/slots/SlotsSettingsPage';
import { StorePage } from 'v2/pages/store/StorePage';
import { StreamerWheelPage } from 'v2/pages/streamerWheel/StreamerWheelPage';
import { SuspiciousUsersPage } from 'v2/pages/suspiciousUsers/SuspiciousUsersPage';
import { TelegramBotPage } from 'v2/pages/telegramBot/TelegramBotPage';
import { TournamentGroupsPage } from 'v2/pages/tournamentGroups/TournamentGroupsPage';
import { TournamentsPage } from 'v2/pages/tournaments/TournamentsPage';
import { TournamentsHistoryPage } from 'v2/pages/tournamentsHistory/TournamentsHistoryPage';
import { UserGuidePage } from 'v2/pages/userGuide/UserGuidePage';
import { UserProfilePage } from 'v2/pages/userProfile/UserProfilePage';
import { UserSettingsPage } from 'v2/pages/userSettings/UserSettingsPage';
import { VideoPage } from 'v2/pages/video/VideoPage';
import { AppRoute } from 'v2/routes';

export const App: FC = () => {
  const routes = createBrowserRouter([
    {
      element: <AppLayout />,
      errorElement: <ErrorLayout />,
      children: [
        {
          path: AppRoute.ROOT,
          element: <RootPage />,
        },
        {
          path: AppRoute.LOGIN_PAGE,
          element: <LoginPage />,
        },
        {
          path: AppRoute.MASTER_KEY_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <MasterKeyPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.FILE_SYSTEM_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <FileSystemPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.ADS_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <AdsSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.CASINO_ADS_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <CasinoAdsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.MAIN_SCREEN_SETTINGS_PAGE,
          element: (
            <RoleGuard group={SELLS_GROUP}>
              <MainScreenSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.PARTNER_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <PartnersPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.TOURNAMENT_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <TournamentsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.CASINO_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <CasinoSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.USER_SETTINGS_PAGE,
          element: (
            <RoleGuard group={SUPPORT_GROUP}>
              <UserSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.USER_PROFILE_PAGE,
          element: (
            <RoleGuard group={SUPPORT_GROUP}>
              <UserProfilePage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.TOURNAMENTS_HISTORY_PAGE,
          element: (
            <RoleGuard group={PRIZE_HEAD_SUPPORT_GROUP}>
              <TournamentsHistoryPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.STREAMER_WHEEL_SETTINGS_PAGE,
          element: (
            <RoleGuard group={WRITE_GROUP}>
              <StreamerWheelPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.SUSPICIOUS_USERS_PAGE,
          element: (
            <RoleGuard group={WRITE_GROUP}>
              <SuspiciousUsersPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.PROMO_CODE_SETTINGS_PAGE,
          element: (
            <RoleGuard group={SERVICE_GROUP}>
              <PromoCodePage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.MODAL_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <ModalSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.TELEGRAM_BOT_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <TelegramBotPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.DAILY_BONUSES_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <DailyBonusesPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.REWARDS_HISTORY_PAGE,
          element: (
            <RoleGuard group={PRIZE_HEAD_SUPPORT_GROUP}>
              <RewardsHistoryPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.PROVIDER_TRANSACTIONS_PAGE,
          element: (
            <RoleGuard group={ALL_ADMIN_GROUP}>
              <ProviderTransactionsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.DISTRIBUTION_LINK_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <DistributionLinkPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.STORE_SETTINGS_PAGE,
          element: (
            <RoleGuard group={WRITE_GROUP}>
              <StorePage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.PROVIDER_FREE_SPINS_SETTINGS_PAGE,
          element: (
            <RoleGuard group={WRITE_GROUP}>
              <FreeSpinsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.PROVIDER_SLOT_SETTINGS_PAGE,
          element: (
            <RoleGuard group={WRITE_GROUP}>
              <SlotsSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.BET_SYSTEM_SETTINGS,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <BetSystemSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.VIDEO_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <VideoPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.LEVEL_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <LevelsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.VIP_SETTINGS_PAGE,
          element: (
            <RoleGuard group={SELLS_GROUP}>
              <VipSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.LOTTERY_SETTINGS,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <LotteriesPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.CHEATER_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <CheatersPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.DOMAIN_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <DomainPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.REFERRAL_REWARD_SETTINGS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <ReferralRewardSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.TICKETS_PAGE,
          element: (
            <RoleGuard group={PRIZE_SERVICE_GROUP}>
              <TicketSettingsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.USER_GUIDES_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <UserGuidePage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.ENTRY_REWARDS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <EntryRewardPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.TOURNAMENT_GROUP_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <TournamentGroupsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.MAILING_SETTINGS_PAGE,
          element: (
            <RoleGuard group={HEAD_SUPPORT_GROUP}>
              <MailingPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.BROADCAST_NOTIFICATIONS_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <BroadcastNotificationsPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.REFERRAL_SYSTEM_V2_PAGE,
          element: (
            <RoleGuard group={MANAGEMENT_GROUP}>
              <ReferralSystemPage />
            </RoleGuard>
          ),
        },
        {
          path: AppRoute.FORBIDDEN,
          element: <ErrorLayout status={403} />,
        },
        {
          path: '*',
          element: <ErrorLayout status={404} />,
        },
      ],
    },
  ]);

  return <RouterProvider router={routes} />;
};
